import { useState } from 'react';
import { useInstallPrompt } from '../hooks/useInstallPrompt';
import { Button } from './Button';
import styles from './InstallTip.module.css';

const DISMISS_KEY = 'whaletail.installTipDismissed.v1';

function readDismissed(): boolean {
  try {
    return window.localStorage.getItem(DISMISS_KEY) === '1';
  } catch {
    return false;
  }
}

function isTouchDevice(): boolean {
  return (
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(pointer: coarse)').matches
  );
}

/**
 * A one-time, dismissible tip on phones: installing WhaleTail to the home
 * screen gives it the whole screen (no browser toolbar over the tab bar) and
 * is what iOS requires before it allows signal notifications.
 */
export function InstallTip() {
  const { state, promptInstall } = useInstallPrompt();
  const [dismissed, setDismissed] = useState(readDismissed);

  if (dismissed || state === 'unavailable' || state === 'installed' || !isTouchDevice()) {
    return null;
  }

  const dismiss = () => {
    try {
      window.localStorage.setItem(DISMISS_KEY, '1');
    } catch {
      // Storage unavailable: the tip simply returns next visit.
    }
    setDismissed(true);
  };

  return (
    <aside className={styles.tip} aria-label="Install WhaleTail">
      <div className={styles.text}>
        <strong>Add WhaleTail to your home screen</strong>
        <span>
          It opens full screen without the browser&apos;s toolbar, and it&apos;s needed
          for signal notifications on iPhone.
          {state === 'ios-manual' && (
            <>
              {' '}
              Tap <strong>Share</strong> (in some layouts it&apos;s under the{' '}
              <strong>More</strong> <span aria-hidden="true">(•••)</span> button), then{' '}
              <strong>Add to Home Screen</strong>.
            </>
          )}
        </span>
      </div>
      <div className={styles.actions}>
        {state === 'installable' && (
          <Button
            variant="primary"
            onClick={async () => {
              await promptInstall();
              // Whatever they chose in the browser's own prompt, don't ask again.
              dismiss();
            }}
          >
            Install
          </Button>
        )}
        <button type="button" className={styles.dismiss} onClick={dismiss}>
          Not now
        </button>
      </div>
    </aside>
  );
}
